// Rechenwerk des Kalenders (A-027 S1): reine Funktionen ohne Oberfläche, damit sie sich einzeln prüfen lassen.
// Zeiten stehen im Termin als "2026-10-26T08:00" (Ortszeit, ohne Zeitzone) – so werden sie auch gelesen und geschrieben.

export const STANDARD_DAUER_MIN = 60;      // Termin ohne Ende: 1 Stunde (nur Anzeige)
export const RASTER_MIN = 15;              // Ziehen rastet auf Viertelstunden ein
export const TAG_VON_STUNDE = 6;           // Standard-Bereich im Kalender
export const TAG_BIS_STUNDE = 20;

const zwei = (n) => String(n).padStart(2, "0");

/** "2026-10-26T08:00" (oder nur Datum) -> Date in Ortszeit; ungültig -> null */
export const leseZeit = (s) => {
  if (!s || typeof s !== "string") return null;
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?(?::\d{2}(?:\.\d+)?)?$/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
  const d = new Date(s); // mit Zeitzone (Z, +02:00): der Browser rechnet in Ortszeit um
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Date -> "2026-10-26T08:00" */
export const schreibeZeit = (d) =>
  `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}T${zwei(d.getHours())}:${zwei(d.getMinutes())}`;

/** "2026-10-26" für Tages-Schlüssel */
export const tagSchluessel = (d) => `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;

/** "2026-10-26" -> Date (0:00 Ortszeit) */
export const leseTag = (k) => { const [j, m, t] = k.split("-").map(Number); return new Date(j, m - 1, t); };

export const tagAnfang = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const plusTage = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
export const plusMinuten = (d, n) => new Date(d.getTime() + n * 60000);
export const gleicherTag = (a, b) => tagSchluessel(a) === tagSchluessel(b);

/** Montag der Woche */
export const wochenStart = (d) => {
  const t = tagAnfang(d);
  const wt = (t.getDay() + 6) % 7; // Montag = 0
  return plusTage(t, -wt);
};

/** Monatsraster: 6 Wochen ab dem Montag vor dem 1. (immer gleich hoch) */
export const monatsTage = (d) => {
  const erster = new Date(d.getFullYear(), d.getMonth(), 1);
  const start = wochenStart(erster);
  return Array.from({ length: 42 }, (_, i) => plusTage(start, i));
};

/** Benutzernamen eines Termins: Liste, sonst das alte Einzelfeld */
export const monteureVon = (t) => {
  const l = Array.isArray(t?.monteur_usernames) ? t.monteur_usernames.filter(Boolean) : [];
  if (l.length) return l;
  return t?.monteur_username ? [t.monteur_username] : [];
};

/** Zeiten eines Termins für die Anzeige. Ohne (oder vor dem Start liegendes) Ende: 1 Stunde. */
export const terminZeiten = (t) => {
  const start = leseZeit(t?.start);
  if (!start) return null;
  let ende = leseZeit(t?.ende);
  if (!ende || ende.getTime() <= start.getTime()) ende = plusMinuten(start, STANDARD_DAUER_MIN);
  return { start, ende };
};

export const ueberlappt = (a, b) => a.start.getTime() < b.ende.getTime() && b.start.getTime() < a.ende.getTime();

/**
 * Doppelbelegung: Monteure, die zur selben Zeit in einem anderen (nicht abgesagten) Termin stehen.
 * kandidat: { id, start: Date, ende: Date, monteure: [..] }
 * Rückgabe: [{ termin, monteure: [gemeinsame Monteure] }]
 */
export const findeKonflikte = (kandidat, termine) => {
  const aus = [];
  for (const t of termine) {
    if (t.id === kandidat.id || t.status === "abgesagt") continue;
    const z = terminZeiten(t);
    if (!z || !ueberlappt(kandidat, z)) continue;
    const gemeinsam = monteureVon(t).filter((m) => kandidat.monteure.includes(m));
    if (gemeinsam.length) aus.push({ termin: t, monteure: gemeinsam });
  }
  return aus;
};

/** Für alle Termine auf einmal: Map id -> [{ termin, monteure }] (nur Termine mit Doppelbelegung) */
export const alleKonflikte = (termine) => {
  const karte = new Map();
  const aktiv = termine.filter((t) => t.status !== "abgesagt");
  for (const t of aktiv) {
    const z = terminZeiten(t);
    if (!z) continue;
    const k = findeKonflikte({ id: t.id, ...z, monteure: monteureVon(t) }, aktiv);
    if (k.length) karte.set(t.id, k);
  }
  return karte;
};

/** Satz für die Warnung, z. B. „Peter Holz ist zur selben Zeit schon bei „Schiebetür Küche“ eingetragen.“ */
export const konfliktText = (konflikte, name = (u) => u) => {
  if (!konflikte.length) return "";
  return konflikte
    .map((k) => `${k.monteure.map(name).join(" und ")} ist zur selben Zeit schon bei „${k.termin.titel}“ eingetragen`)
    .join("; ") + ".";
};

/** Ausschnitt eines Termins an einem Tag (Minuten ab 0:00), null wenn er den Tag nicht berührt */
export const ausschnittAmTag = (z, tag) => {
  const von = tagAnfang(tag).getTime();
  const bis = plusTage(tagAnfang(tag), 1).getTime();
  if (z.ende.getTime() <= von || z.start.getTime() >= bis) return null;
  const s = Math.max(z.start.getTime(), von);
  const e = Math.min(z.ende.getTime(), bis);
  return {
    von: Math.round((s - von) / 60000),
    bis: Math.round((e - von) / 60000),
    beginntHier: z.start.getTime() >= von,
    endetHier: z.ende.getTime() <= bis,
  };
};

/**
 * Nebeneinander legen, wenn sich Ausschnitte in einer Spalte überlappen.
 * eintraege: [{ von, bis, ... }] -> dieselben Einträge mit spur und spuren
 */
export const spurenVergeben = (eintraege) => {
  const sortiert = [...eintraege].sort((a, b) => a.von - b.von || b.bis - a.bis);
  const aus = [];
  let gruppe = [];
  let gruppeEnde = -1;
  const abschliessen = () => {
    const n = gruppe.reduce((m, e) => Math.max(m, e.spur + 1), 1);
    gruppe.forEach((e) => aus.push({ ...e, spuren: n }));
    gruppe = [];
  };
  for (const e of sortiert) {
    if (gruppe.length && e.von >= gruppeEnde) abschliessen();
    const belegt = new Set(gruppe.filter((g) => g.bis > e.von).map((g) => g.spur));
    let spur = 0;
    while (belegt.has(spur)) spur += 1;
    gruppe.push({ ...e, spur });
    gruppeEnde = Math.max(gruppeEnde, e.bis);
  }
  if (gruppe.length) abschliessen();
  return aus;
};

/** Sichtbarer Stundenbereich: Standard 6–20 Uhr, wird größer, wenn ein Termin außerhalb liegt (nichts bleibt versteckt) */
export const stundenBereich = (ausschnitte) => {
  let von = TAG_VON_STUNDE;
  let bis = TAG_BIS_STUNDE;
  for (const a of ausschnitte) {
    von = Math.min(von, Math.floor(a.von / 60));
    bis = Math.max(bis, Math.ceil(a.bis / 60));
  }
  return { von: Math.max(0, von), bis: Math.min(24, bis) };
};

/** Minuten auf das Raster runden */
export const runden = (min, raster = RASTER_MIN) => Math.round(min / raster) * raster;

/**
 * Urlaub/Krank: Welcher Mitarbeiter ist welcher Benutzer?
 *  1. Verknüpfung am Mitarbeiter (Feld `username`, Mitarbeiter-Seite „Anmeldung“) – gilt immer.
 *  2. Nur als Rückfall, solange nichts verknüpft ist: gleicher Vor- und Nachname (Groß-/Kleinschreibung egal) oder gleiche
 *     E-Mail, und nur bei genau einem Treffer, der nicht schon einem anderen Mitarbeiter gehört. Sonst bleibt der Eintrag weg.
 * mitarbeiter: [{id, vorname, nachname, email, username}], benutzer: [{username, anzeige_name, email?}]
 * Rückgabe: Map mitarbeiter_id -> username
 */
export const mitarbeiterZuBenutzer = (mitarbeiter, benutzer) => {
  const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
  const karte = new Map();
  const vergeben = new Set();
  for (const m of mitarbeiter || []) {
    const u = (m.username || "").trim();
    if (u) { karte.set(m.id, u); vergeben.add(u); }
  }
  for (const m of mitarbeiter || []) {
    if (karte.has(m.id)) continue;
    const name = norm(`${m.vorname || ""} ${m.nachname || ""}`);
    const mail = norm(m.email);
    const treffer = (benutzer || []).filter((u) => !vergeben.has(u.username) &&
      ((name && norm(u.anzeige_name) === name) || (mail && norm(u.email) === mail)));
    if (treffer.length === 1) karte.set(m.id, treffer[0].username);
  }
  return karte;
};

/** Abwesenheit (von/bis als Datum) am Tag? */
export const abwesendAmTag = (a, tag) => {
  const von = leseZeit(a.von);
  const bis = leseZeit(a.bis) || von;
  if (!von) return false;
  const t = tagAnfang(tag).getTime();
  return tagAnfang(von).getTime() <= t && t <= tagAnfang(bis).getTime();
};

// ---------- Nächste freie Zeit (Planung) ----------
export const GANZER_TAG_MIN = 480;

/**
 * Erster freier Platz ab `ab` für ALLE genannten Monteure: an den Arbeitstagen in der Arbeitszeit des Betriebs
 * (arbeitszeit = { tage: [0..6, So=0], vonMin, bisMin }, Standard Mo–Fr 7–17 Uhr), nicht an Urlaubs-/Krank-Tagen,
 * nicht über einem vorhandenen (nicht abgesagten) Termin eines der Monteure. Viertelstunden-Raster.
 * Rückgabe: { start: Date, ende: Date } oder null (nichts in 90 Tagen).
 */
export const naechsteFreieZeit = ({ ab, dauerMin = 60, monteure = [], termine = [], abwesenheiten = [], arbeitszeit = { tage: [1, 2, 3, 4, 5], vonMin: 420, bisMin: 1020 } }) => {
  if (!monteure.length) return null;
  const start0 = new Date(ab.getTime());
  const q = RASTER_MIN;
  for (let i = 0; i < 90; i++) {
    const tag = plusTage(tagAnfang(start0), i);
    if (!arbeitszeit.tage.includes(tag.getDay())) continue;
    if (abwesenheiten.some((a) => monteure.includes(a.username) && abwesendAmTag(a, tag))) continue;
    const von = plusMinuten(tag, arbeitszeit.vonMin);
    const schluss = plusMinuten(tag, arbeitszeit.bisMin);
    let k = von;
    if (k.getTime() < start0.getTime()) {
      const rest = Math.ceil((start0.getTime() - k.getTime()) / 60000 / q) * q;
      k = plusMinuten(von, rest);
    }
    for (; k.getTime() + dauerMin * 60000 <= schluss.getTime(); k = plusMinuten(k, q)) {
      const ende = plusMinuten(k, dauerMin);
      if (!findeKonflikte({ id: "", start: k, ende, monteure }, termine).length) return { start: k, ende };
    }
  }
  return null;
};
